import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Dropzone from "react-dropzone";
import {
  Field,
  Input,
  Spinner,
  Toaster,
  Tooltip,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Edit24Filled,
  ArrowUpload16Regular,
  ArrowDownload20Regular,
} from "@fluentui/react-icons";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  getCompanyDetails,
  updateCompanyDetails,
} from "../../../redux/SystemManager/companyDetailsSlice";
import apiServices from "../../../service";
import ASCButton from "../../../components/Button";
import DynamicDropzone from "../../../components/DynamicDropzone/Dropzone";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { checkUserPermission } from "../../../utils";
import { UserPermissionsList } from "../../../config";

// Client-side mirror of masterdata's UpdateCompanyDetailsDto constraints
// (src/microservices/masterdata/src/company-details/dto/update-company-details.dto.ts)
// so the form fails fast instead of round-tripping to the backend first.
const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB, per the mockup's stated limit
const ALLOWED_LOGO_TYPES = ["image/png", "image/webp"];

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "12px",
  },
  panel: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "20px 22px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    columnGap: "28px",
    rowGap: "20px",
    marginBottom: "18px",
  },
  actions: {
    display: "flex",
    gap: "10px",
  },
  logoRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
  },
  logoBox: {
    position: "relative",
    width: "64px",
    height: "64px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  logoDownloadOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    color: "#fff",
    cursor: "pointer",
  },
  logoImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  logoPlaceholder: {
    fontSize: "10.5px",
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    padding: "0 6px",
  },
  logoHint: {
    fontSize: "11.5px",
    color: tokens.colorNeutralForeground3,
    marginTop: "6px",
  },
  // Fluent's default disabled Input styling dims the text quite heavily —
  // fine for a genuinely inert control, but this "view" state still needs
  // to be read comfortably (it's the default screen, not an edge case).
  // Overrides opacity/color on the underlying <input> specifically, rather
  // than the wrapper, since Fluent's own disabled dimming is applied there.
  readOnlyField: {
    "& input": {
      opacity: 0.55,
      color: tokens.colorNeutralForeground1,
      WebkitTextFillColor: tokens.colorNeutralForeground1,
    },
  },
  logoDropzone: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "32px",
    padding: "0 14px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    userSelect: "none",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
});

type CompanyFormValues = {
  companyName: string;
  displayName: string;
  phoneNumber: string;
  whatsappNumber: string;
  address: string;
};

function BasicDetails() {
  const styles = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { showToast, toasterId } = useToastMessage();

  const authUser = useSelector((state: RootState) => state.authUser);
  const { data, loading, saveLoading } = useSelector(
    (state: RootState) => state.companyDetails
  );

  const canEdit = checkUserPermission(
    authUser,
    UserPermissionsList.ALLOW_UPDATE_MASTER_DATA
  );

  const [mode, setMode] = useState<"view" | "edit">("view");

  // The currently-saved logo (fetched as an authenticated blob) — kept
  // entirely separate from the "new file picked this session" state below.
  // Cancel never touches this, which is what keeps the existing logo
  // visible after Edit -> Cancel instead of flashing "No logo".
  const [savedLogoPreviewUrl, setSavedLogoPreviewUrl] = useState<string | null>(
    null
  );
  const [savedLogoLoading, setSavedLogoLoading] = useState(false);
  const savedObjectUrlRef = useRef<string | null>(null);
  const savedLogoBlobRef = useRef<Blob | null>(null);
  const [logoHover, setLogoHover] = useState(false);

  // A newly-picked, not-yet-saved logo file for this edit session, and its
  // instant local preview + DynamicDropzone's own upload/scan status —
  // reusing the same component/animation used elsewhere in the app
  // (see src/pages/systemManager/templateEngine/LetterTemplates.tsx) rather
  // than a bespoke upload/poll implementation.
  const [rawLogoFiles, setRawLogoFiles] = useState<File[]>([]);
  const [scannedLogoFiles, setScannedLogoFiles] = useState<any[]>([]);
  const [localLogoPreviewUrl, setLocalLogoPreviewUrl] = useState<string | null>(
    null
  );
  const localObjectUrlRef = useRef<string | null>(null);

  const pendingLogo = scannedLogoFiles[0];
  const logoInFlight =
    rawLogoFiles.length > 0 &&
    pendingLogo?.status !== "success" &&
    pendingLogo?.status !== "error";

  useEffect(() => {
    dispatch(getCompanyDetails());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik<CompanyFormValues>({
    initialValues: {
      companyName: data?.CompanyName || "",
      displayName: data?.DisplayName || "",
      phoneNumber: data?.PhoneNumber || "",
      whatsappNumber: data?.WhatsappNumber || "",
      address: data?.Address || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      companyName: Yup.string()
        .required("Company Name is required.")
        .max(255, "Company Name cannot be longer than 255 characters."),
      displayName: Yup.string().max(
        255,
        "Display Name cannot be longer than 255 characters."
      ),
      // Max-length only, deliberately no format/regex check (peer-review
      // question) — international phone/WhatsApp number formats vary too
      // widely (country codes, spacing, punctuation) to validate reliably
      // client-side; the backend DTO makes the same choice. Tracked as an
      // open question in the architecture doc, not an oversight.
      phoneNumber: Yup.string().max(
        50,
        "Phone Number cannot be longer than 50 characters."
      ),
      whatsappNumber: Yup.string().max(
        50,
        "Whatsapp Number cannot be longer than 50 characters."
      ),
      address: Yup.string().max(
        500,
        "Address cannot be longer than 500 characters."
      ),
    }),
    onSubmit: (values) => {
      handleSave(values);
    },
  });

  // Fetches the saved logo whenever it changes (initial load, or after a
  // save that set a new one) — independent of any in-progress edit, so
  // Edit -> Cancel never re-triggers or clears this.
  useEffect(() => {
    let cancelled = false;
    if (savedObjectUrlRef.current) {
      URL.revokeObjectURL(savedObjectUrlRef.current);
      savedObjectUrlRef.current = null;
    }

    if (data?.Logo) {
      setSavedLogoLoading(true);
      setSavedLogoPreviewUrl(null);
      apiServices.documentDownload
        .download(data.Logo)
        .then((res: any) => {
          if (cancelled) return;
          savedLogoBlobRef.current = res.data;
          const url = URL.createObjectURL(res.data);
          savedObjectUrlRef.current = url;
          setSavedLogoPreviewUrl(url);
        })
        .catch(() => {
          if (!cancelled) setSavedLogoPreviewUrl(null);
        })
        .finally(() => {
          if (!cancelled) setSavedLogoLoading(false);
        });
    } else {
      setSavedLogoPreviewUrl(null);
      setSavedLogoLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [data?.Logo]);

  // Instant local preview of whatever's currently picked this session.
  useEffect(() => {
    if (localObjectUrlRef.current) {
      URL.revokeObjectURL(localObjectUrlRef.current);
      localObjectUrlRef.current = null;
    }
    if (rawLogoFiles[0]) {
      const url = URL.createObjectURL(rawLogoFiles[0]);
      localObjectUrlRef.current = url;
      setLocalLogoPreviewUrl(url);
    } else {
      setLocalLogoPreviewUrl(null);
    }
  }, [rawLogoFiles]);

  useEffect(() => {
    return () => {
      if (savedObjectUrlRef.current) URL.revokeObjectURL(savedObjectUrlRef.current);
      if (localObjectUrlRef.current) URL.revokeObjectURL(localObjectUrlRef.current);
    };
  }, []);

  const enterEditMode = () => setMode("edit");

  // Only discards a pending, not-yet-saved logo pick — never touches the
  // saved-logo preview above, which is the actual fix for the "logo
  // disappears after Cancel" bug (that used to share the same state).
  const discardPendingLogo = () => {
    setRawLogoFiles([]);
    setScannedLogoFiles([]);
  };

  const cancelEdit = () => {
    formik.resetForm();
    discardPendingLogo();
    setMode("view");
    showToast("Discarded", "Changes discarded.", "warning");
  };

  const handleLogoDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      showToast("Invalid file", "Only PNG or WebP images are allowed.", "error");
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      showToast("File too large", "Logo must be 2MB or smaller.", "error");
      return;
    }

    setRawLogoFiles([file]);
    setScannedLogoFiles([]);
  };

  const handleLogoFilesChange = (files: any[]) => {
    setScannedLogoFiles(files);
  };

  // Reuses the blob already fetched for the preview (or the raw picked
  // File, for a not-yet-saved pick) — no extra request, no new endpoint.
  const handleDownloadLogo = () => {
    const hasLocalPick = rawLogoFiles.length > 0 && localLogoPreviewUrl;
    const url = hasLocalPick ? localLogoPreviewUrl : savedLogoPreviewUrl;
    if (!url) return;

    const mimeType = hasLocalPick
      ? rawLogoFiles[0].type
      : savedLogoBlobRef.current?.type;
    const ext = mimeType === "image/webp" ? "webp" : "png";

    const link = document.createElement("a");
    link.href = url;
    link.download = `company-logo.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogoFileRemoved = (removedFile: File) => {
    setRawLogoFiles((prev) => prev.filter((f) => f !== removedFile));
    setScannedLogoFiles([]);
  };

  const handleSave = async (values: CompanyFormValues) => {
    let logoObjectId: string | undefined;

    if (rawLogoFiles.length > 0) {
      if (!pendingLogo || pendingLogo.status !== "success" || !pendingLogo.objectId) {
        showToast(
          "Please wait",
          "Please wait for the logo to finish uploading before saving.",
          "warning"
        );
        return;
      }

      // No direct call to Document Service's /document/files/finalize here
      // (WAT-I2648 follow-up) — the objectId from the upload step above is
      // sent as-is, and masterdata's backend finalizes it server-to-server
      // as part of the save itself, then validates/stores the resulting
      // documentId. See masterdata's MasterdataService.upsertCompanyProfile.
      logoObjectId = pendingLogo.objectId;
    }

    // Only fields actually present in the request are updated server-side —
    // omit `logo` entirely unless a new file was uploaded this session, so
    // an unrelated text-only save can never clobber the existing logo.
    const payload: any = {
      companyName: values.companyName.trim(),
      displayName: values.displayName.trim(),
      phoneNumber: values.phoneNumber.trim(),
      whatsappNumber: values.whatsappNumber.trim(),
      address: values.address.trim(),
    };
    if (logoObjectId) payload.logo = logoObjectId;

    const result = await dispatch(updateCompanyDetails(payload));
    if (updateCompanyDetails.fulfilled.match(result)) {
      setRawLogoFiles([]);
      setScannedLogoFiles([]);
      setMode("view");
      showToast("Saved", "Company details saved successfully.");
    } else {
      showToast(
        "Error",
        (result.payload as string) || "Failed to save company details.",
        "error"
      );
    }
  };

  const isBusy = saveLoading || logoInFlight;

  return (
    <div>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />

      <div className={styles.toolbar}>
        {mode === "view" && canEdit && (
          <ASCButton
            shape="rounded"
            icon={<Edit24Filled />}
            label="Edit"
            onClick={enterEditMode}
            disabled={loading}
          />
        )}
      </div>

      {loading && !data ? (
        <Spinner label="Loading company details…" />
      ) : (
        <div className={styles.panel}>
          <div className={styles.grid}>
            <Field
              label="Company Name"
              required
              validationState={
                formik.touched.companyName && formik.errors.companyName
                  ? "error"
                  : undefined
              }
              validationMessage={
                formik.touched.companyName ? formik.errors.companyName : undefined
              }
            >
              <Input
                name="companyName"
                value={formik.values.companyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={mode === "view"}
                className={mode === "view" ? styles.readOnlyField : undefined}
                placeholder="e.g. Wealth Assyst Ltd"
              />
            </Field>

            <Field
              label="Display Name"
              validationState={
                formik.touched.displayName && formik.errors.displayName
                  ? "error"
                  : undefined
              }
              validationMessage={
                formik.touched.displayName ? formik.errors.displayName : undefined
              }
            >
              <Input
                name="displayName"
                value={formik.values.displayName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={mode === "view"}
                className={mode === "view" ? styles.readOnlyField : undefined}
                placeholder="e.g. Wealth Assyst"
              />
            </Field>

            <Field
              label="Phone Number"
              validationState={
                formik.touched.phoneNumber && formik.errors.phoneNumber
                  ? "error"
                  : undefined
              }
              validationMessage={
                formik.touched.phoneNumber ? formik.errors.phoneNumber : undefined
              }
            >
              <Input
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={mode === "view"}
                className={mode === "view" ? styles.readOnlyField : undefined}
                placeholder="e.g. +44 20 7946 0958"
              />
            </Field>

            <Field
              label="Whatsapp Number"
              validationState={
                formik.touched.whatsappNumber && formik.errors.whatsappNumber
                  ? "error"
                  : undefined
              }
              validationMessage={
                formik.touched.whatsappNumber
                  ? formik.errors.whatsappNumber
                  : undefined
              }
            >
              <Input
                name="whatsappNumber"
                value={formik.values.whatsappNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={mode === "view"}
                className={mode === "view" ? styles.readOnlyField : undefined}
                placeholder="e.g. +44 7700 900123"
              />
            </Field>

            <Field
              label="Address"
              validationState={
                formik.touched.address && formik.errors.address ? "error" : undefined
              }
              validationMessage={
                formik.touched.address ? formik.errors.address : undefined
              }
            >
              <Input
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={mode === "view"}
                className={mode === "view" ? styles.readOnlyField : undefined}
                placeholder="e.g. 12 Kings Road, London, EC1A 1AA"
              />
            </Field>

            <Field label="Company Logo">
              <div className={styles.logoRow}>
                <div
                  className={styles.logoBox}
                  onMouseEnter={() => setLogoHover(true)}
                  onMouseLeave={() => setLogoHover(false)}
                >
                  {rawLogoFiles.length > 0 && localLogoPreviewUrl ? (
                    <img
                      src={localLogoPreviewUrl}
                      alt="Selected logo"
                      className={styles.logoImg}
                    />
                  ) : savedLogoLoading ? (
                    <Spinner size="tiny" />
                  ) : savedLogoPreviewUrl ? (
                    <img
                      src={savedLogoPreviewUrl}
                      alt="Company logo"
                      className={styles.logoImg}
                    />
                  ) : (
                    <span className={styles.logoPlaceholder}>No logo</span>
                  )}
                  {logoHover &&
                    (localLogoPreviewUrl || savedLogoPreviewUrl) &&
                    !savedLogoLoading && (
                      <Tooltip
                        content="Download logo"
                        relationship="label"
                        positioning="below"
                      >
                        <div
                          className={styles.logoDownloadOverlay}
                          onClick={handleDownloadLogo}
                        >
                          <ArrowDownload20Regular />
                        </div>
                      </Tooltip>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {mode === "edit" && rawLogoFiles.length === 0 && (
                    <Dropzone
                      multiple={false}
                      accept={{ "image/png": [".png"], "image/webp": [".webp"] }}
                      onDrop={handleLogoDrop}
                    >
                      {({ getRootProps, getInputProps }) => (
                        <div {...getRootProps()} className={styles.logoDropzone}>
                          <input {...getInputProps()} />
                          {/* <ArrowUpload16Regular /> */}
                          Choose logo
                        </div>
                      )}
                    </Dropzone>
                  )}
                  {rawLogoFiles.length > 0 && (
                    <DynamicDropzone
                      multiple={false}
                      externalFiles={rawLogoFiles}
                      onFilesChange={handleLogoFilesChange}
                      onFileRemoved={handleLogoFileRemoved}
                      userId={authUser?.userId}
                      style={{ marginTop: 0, maxWidth: "320px" }}
                    />
                  )}
                  <div className={styles.logoHint}>PNG or WebP, up to 2MB.</div>
                </div>
              </div>
            </Field>
          </div>

          {mode === "edit" && (
            <div className={styles.actions}>
              <ASCButton
                shape="rounded"
                appearance="primary"
                className="asc-button-primary"
                label={saveLoading ? "Saving…" : "Save"}
                icon={saveLoading ? <Spinner size="tiny" /> : undefined}
                onClick={() => formik.handleSubmit()}
                disabled={isBusy}
              />
              <ASCButton
                shape="rounded"
                label="Cancel"
                onClick={cancelEdit}
                disabled={isBusy}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BasicDetails;
